import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { HunterAffiliateDescription } from "Components/Affiliate/Hunter/HunterAffiliateDescription";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { AffiliateLinkWithActions } from "Components/Affiliate/AffiliateLinkWithActions";
import { ButtonLink } from "Components/Shared/ButtonLink";
import { useSearchParams } from "Navigation/useSearchParams";
import { CustomerAffiliateDescription } from "Components/Affiliate/Customer/CustomerAffiliateDescription";
import { MerchantAffiliateDescription } from "Components/Affiliate/Merchant/MerchantAffiliateDescription";
import { MerchantAffiliateLicenseSelector } from "Components/Affiliate/Merchant/MerchantAffiliateLicenseSelector";
import { AffiliateEarningsSummary } from "Components/Affiliate/Earnings/AffiliateEarningsSummary";
import { Resources, useResource } from "Translations/Resources";
import { LeadStatus } from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { FilteredLeadList } from "Components/Affiliate/Stats/FilteredLeadList";
import type { AffiliateTabItem } from "Components/Affiliate/types";
import { appUrl } from "Utils/UrlUtils";

const RESOURCES = Resources.Components.Affiliate.HunterAffiliate;

export const HunterAffiliatePageContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useResource();

  const tabs: Array<AffiliateTabItem> = [
    {
      id: "hunter",
      label: t(RESOURCES.Tabs.Hunter),
      content: (
        <Stack gap={4}>
          <PrimaryPaper>
            <HunterAffiliateDescription />
            <AffiliateLinkWithActions
              invitedUserRole={AccessRoleCodes.Hunter}
            />
          </PrimaryPaper>
          <Stack gap={1}>
            <FilteredLeadList
              leadStatus={LeadStatus.Invited}
              invitedAsRole={AccessRoleCodes.Hunter}
            />
            <FilteredLeadList
              registeredAsRole={AccessRoleCodes.Hunter}
              leadStatus={LeadStatus.Registered}
            />
            <FilteredLeadList
              registeredAsRole={AccessRoleCodes.Hunter}
              leadStatus={LeadStatus.Completed}
            />
          </Stack>
        </Stack>
      ),
    },
    {
      id: "customer",
      label: t(RESOURCES.Tabs.Customer),
      content: (
        <Stack gap={4}>
          <AffiliateEarningsSummary />
          <PrimaryPaper>
            <CustomerAffiliateDescription />
            <AffiliateLinkWithActions
              invitedUserRole={AccessRoleCodes.Customer}
            />
          </PrimaryPaper>
          <Stack gap={1}>
            <FilteredLeadList
              leadStatus={LeadStatus.Invited}
              invitedAsRole={AccessRoleCodes.Customer}
            />
            <FilteredLeadList
              registeredAsRole={AccessRoleCodes.Customer}
              leadStatus={LeadStatus.Registered}
            />
          </Stack>
        </Stack>
      ),
    },
    {
      id: "merchant",
      label: t(RESOURCES.Tabs.Merchant),
      content: (
        <Stack gap={4}>
          <PrimaryPaper>
            <MerchantAffiliateDescription />
            <MerchantAffiliateLicenseSelector />
            <AffiliateLinkWithActions invitedUserRole="Merchant" />
          </PrimaryPaper>
          {/* ZS-1359 Q1: shortcut for inviting someone who already has a Mo.one account.
              Routes the Hunter through the existing merchant-create flow whose SendAccessForm
              already runs an identity-lookup (4.5-A) and shows a branch hint when the entered
              email matches an existing Identity. The actual invitation lands in the invitee's
              in-app inbox via Branch A (RoleInvitation), not as a registration link. */}
          <PrimaryPaper>
            <Stack gap={2}>
              <Typography variant="h6">
                {t(RESOURCES.InviteExistingUser.Title)}
              </Typography>
              <Typography variant="body2">
                {t(RESOURCES.InviteExistingUser.Description)}
              </Typography>
              <Box>
                <ButtonLink
                  link={appUrl("merchant-new-registration")}
                  text={t(RESOURCES.InviteExistingUser.ButtonLabel)}
                  variant="outlined"
                />
              </Box>
            </Stack>
          </PrimaryPaper>
          <Stack gap={1}>
            <FilteredLeadList
              leadStatus={LeadStatus.Invited}
              invitedAsRole="Merchant"
            />
            <FilteredLeadList
              registeredAsRole="Merchant"
              leadStatus={LeadStatus.Registered}
            />
          </Stack>
        </Stack>
      ),
    },
  ];

  const activeTab = searchParams.get("activeTab") || tabs[0].id;

  return (
    <Box display="flex" alignItems="center" flexDirection="column">
      <Tabs
        value={activeTab}
        onChange={(_e, id: string) => setSearchParams({ activeTab: id })}
        variant="scrollable"
        sx={{
          width: "100%",
          "& .MuiTabs-scrollButtons.Mui-disabled": {
            opacity: 0.3,
          },
        }}
      >
        {tabs.map(({ label, id }) => (
          <Tab label={label} value={id} key={id} />
        ))}
      </Tabs>
      <Box mt="0.5rem" width="100%" maxWidth="600px">
        <Stack gap={4}>
          {tabs.map(({ content, id }) => (
            <div role="tabpanel" hidden={id !== activeTab} key={id}>
              {id === activeTab && content}
            </div>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};
